/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, localStorage, window */

const SETTINGS_KEY = "obsidian-exporter-settings";
let appointmentData = null;

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    loadSettings();
    loadAppointmentData();
    document.getElementById("obsidian-btn").onclick = sendToObsidian;
    document.getElementById("save-settings-btn").onclick = saveSettings;
  }
});

// Settings management using Office roaming settings
function loadSettings() {
  try {
    const roamingSettings = Office.context.roamingSettings;
    const vaultName = roamingSettings.get("vaultName");
    const folderPath = roamingSettings.get("folderPath");
    const removeDialIn = roamingSettings.get("removeDialIn");

    if (vaultName) document.getElementById("vault-name").value = vaultName;
    if (folderPath) document.getElementById("folder-path").value = folderPath;
    document.getElementById("remove-dialin").checked = removeDialIn === true;
  } catch (e) {
    console.error("Failed to load settings:", e);
  }
}

function saveSettings() {
  try {
    const roamingSettings = Office.context.roamingSettings;
    roamingSettings.set("vaultName", document.getElementById("vault-name").value.trim());
    roamingSettings.set("folderPath", document.getElementById("folder-path").value.trim());
    roamingSettings.set("removeDialIn", document.getElementById("remove-dialin").checked);

    roamingSettings.saveAsync(function(result) {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        showStatus("Settings saved!", "success");
      } else {
        showStatus("Failed to save settings", "error");
      }
    });
  } catch (e) {
    showStatus("Failed to save settings", "error");
  }
}

function getSettings() {
  return {
    vaultName: document.getElementById("vault-name").value.trim(),
    folderPath: document.getElementById("folder-path").value.trim(),
    removeDialIn: document.getElementById("remove-dialin").checked,
  };
}

async function loadAppointmentData() {
  const item = Office.context.mailbox.item;
  const previewContainer = document.getElementById("appointment-preview");

  try {
    // Collect basic properties
    appointmentData = {
      subject: item.subject || "Untitled Appointment",
      start: item.start,
      end: item.end,
      location: item.location || "",
      organizer: item.organizer,
      requiredAttendees: item.requiredAttendees || [],
      optionalAttendees: item.optionalAttendees || [],
      body: "",
      recurrence: null,
    };

    // Get body content asynchronously
    const bodyPromise = new Promise((resolve) => {
      item.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          resolve("");
        }
      });
    });

    // Get recurrence if available
    let recurrencePromise = Promise.resolve(null);
    if (item.recurrence) {
      // Check if it's an async method or direct value
      if (typeof item.recurrence.getAsync === "function") {
        recurrencePromise = new Promise((resolve) => {
          item.recurrence.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value);
            } else {
              resolve(null);
            }
          });
        });
      } else {
        // Direct value (new Outlook)
        recurrencePromise = Promise.resolve(item.recurrence);
      }
    }

    // Wait for async properties
    const [body, recurrence] = await Promise.all([bodyPromise, recurrencePromise]);
    appointmentData.body = body;
    appointmentData.recurrence = recurrence;

    // Render preview
    renderPreview(previewContainer);
  } catch (error) {
    previewContainer.innerHTML = `<p class="error">Error loading appointment: ${error.message}</p>`;
  }
}

function renderPreview(container) {
  const data = appointmentData;

  let html = `
    <div class="preview-item">
      <span class="preview-label">Subject:</span>
      <span class="preview-value">${escapeHtml(data.subject)}</span>
    </div>
    <div class="preview-item">
      <span class="preview-label">Date:</span>
      <span class="preview-value">${formatDate(data.start)}</span>
    </div>
    <div class="preview-item">
      <span class="preview-label">Time:</span>
      <span class="preview-value">${formatTime(data.start)} - ${formatTime(data.end)}</span>
    </div>
  `;

  if (data.location) {
    html += `
      <div class="preview-item">
        <span class="preview-label">Location:</span>
        <span class="preview-value">${escapeHtml(data.location)}</span>
      </div>
    `;
  }

  if (data.organizer) {
    html += `
      <div class="preview-item">
        <span class="preview-label">Organizer:</span>
        <span class="preview-value">${escapeHtml(data.organizer.displayName)}</span>
      </div>
    `;
  }

  if (data.requiredAttendees.length > 0) {
    html += `
      <div class="preview-item">
        <span class="preview-label">Required Attendees:</span>
        <span class="preview-value">${data.requiredAttendees.length} participant(s)</span>
      </div>
    `;
  }

  if (data.optionalAttendees.length > 0) {
    html += `
      <div class="preview-item">
        <span class="preview-label">Optional Attendees:</span>
        <span class="preview-value">${data.optionalAttendees.length} participant(s)</span>
      </div>
    `;
  }

  if (data.recurrence) {
    html += `
      <div class="preview-item">
        <span class="preview-label">Recurrence:</span>
        <span class="preview-value">${formatRecurrence(data.recurrence)}</span>
      </div>
    `;
  }

  container.innerHTML = html;
}

// Obsidian integration
function sendToObsidian() {
  if (!appointmentData) {
    showStatus("No appointment data loaded", "error");
    return;
  }

  const settings = getSettings();

  if (!settings.vaultName) {
    showStatus("Please configure vault name in Settings", "error");
    // Open settings
    document.querySelector(".settings-section").open = true;
    document.getElementById("vault-name").focus();
    return;
  }

  const markdown = generateMarkdownForObsidian(appointmentData);
  const filename = sanitizeFilenameForObsidian(appointmentData.subject);

  // Build file path
  let filePath = filename;
  if (settings.folderPath) {
    // Remove leading/trailing slashes and normalize
    const folder = settings.folderPath.replace(/^\/+|\/+$/g, "");
    filePath = `${folder}/${filename}`;
  }

  // Build Obsidian URL manually with encodeURIComponent
  // URLSearchParams encodes spaces as '+' but Obsidian needs '%20'
  const obsidianUrl =
    `obsidian://new?vault=${encodeURIComponent(settings.vaultName)}` +
    `&file=${encodeURIComponent(filePath)}` +
    `&content=${encodeURIComponent(markdown)}`;

  // Open Obsidian
  window.open(obsidianUrl, "_blank");
  showStatus("Sent to Obsidian!", "success");
}

function exportToMarkdown() {
  if (!appointmentData) {
    showStatus("No appointment data loaded", "error");
    return;
  }

  const markdown = generateMarkdown(appointmentData);
  const filename = sanitizeFilename(appointmentData.subject) + ".md";

  downloadFile(markdown, filename, "text/markdown");
  showStatus("Downloaded!", "success");
}

function generateMarkdown(data) {
  let md = `# ${data.subject}\n\n`;
  md += generateMarkdownBody(data, getSettings());
  return md;
}

function generateMarkdownForObsidian(data) {
  // Skip the H1 title since Obsidian uses the filename as title
  return generateFrontmatter(data) + generateMarkdownBody(data, getSettings());
}

function generateFrontmatter(data) {
  const created = formatISODate(new Date());
  const date = formatISODateTime(data.start);
  const organizer = data.organizer ? data.organizer.emailAddress : "";

  // Collect all participant emails
  const participants = [];
  if (data.requiredAttendees) {
    data.requiredAttendees.forEach((a) => participants.push(a.emailAddress));
  }
  if (data.optionalAttendees) {
    data.optionalAttendees.forEach((a) => participants.push(a.emailAddress));
  }

  let yaml = "---\n";
  yaml += "type: meeting\n";
  yaml += "tags:\n";
  yaml += `created: ${created}\n`;
  yaml += `date: ${date}\n`;
  yaml += `organizer: ${organizer}\n`;
  yaml += "participants:\n";
  participants.forEach((email) => {
    yaml += `  - ${email}\n`;
  });
  yaml += "parent:\n";
  yaml += "---\n";
  yaml += "***\n";

  return yaml;
}

function formatISODate(date) {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatISODateTime(date) {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function generateMarkdownBody(data, settings) {
  let md = "";

  // Details section
  md += `# Details\n\n`;
  md += `- **Date**: ${formatDate(data.start)}\n`;
  md += `- **Time**: ${formatTime(data.start)} - ${formatTime(data.end)}\n`;

  if (data.location) {
    md += `- **Location**: ${data.location}\n`;
  }

  if (data.organizer) {
    md += `- **Organizer**: ${data.organizer.displayName} (${data.organizer.emailAddress})\n`;
  }

  // Recurrence
  if (data.recurrence) {
    md += `- **Recurrence**: ${formatRecurrence(data.recurrence)}\n`;
  }

  md += `\n`;

  // Participants section
  if (data.requiredAttendees.length > 0 || data.optionalAttendees.length > 0) {
    md += `# Participants\n\n`;

    if (data.requiredAttendees.length > 0) {
      md += `## Required\n\n`;
      data.requiredAttendees.forEach((attendee) => {
        const status = attendee.appointmentResponse ? ` (${formatResponseStatus(attendee.appointmentResponse)})` : "";
        md += `- ${attendee.displayName} (${attendee.emailAddress})${status}\n`;
      });
      md += `\n`;
    }

    if (data.optionalAttendees.length > 0) {
      md += `## Optional\n\n`;
      data.optionalAttendees.forEach((attendee) => {
        const status = attendee.appointmentResponse ? ` (${formatResponseStatus(attendee.appointmentResponse)})` : "";
        md += `- ${attendee.displayName} (${attendee.emailAddress})${status}\n`;
      });
      md += `\n`;
    }
  }

  // Description section
  if (data.body && data.body.trim()) {
    let bodyText = data.body.trim();
    if (settings && settings.removeDialIn) {
      bodyText = removeTeamsDialInInfo(bodyText);
    }
    if (bodyText) {
      md += `# Description\n\n`;
      md += bodyText + "\n\n";
    }
  }

  // Notes section for user to fill in
  md += `# Notes\n\n`;
  
  return md;
}

function formatDate(date) {
  if (!date) return "Unknown";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatTime(date) {
  if (!date) return "Unknown";
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatRecurrence(recurrence) {
  if (!recurrence) return "None";

  const type = recurrence.recurrenceType;
  const props = recurrence.recurrenceProperties || {};

  switch (type) {
    case "daily":
      return props.interval === 1 ? "Daily" : `Every ${props.interval} days`;
    case "weekly":
      const days = props.days ? props.days.join(", ") : "";
      return props.interval === 1 ? `Weekly on ${days}` : `Every ${props.interval} weeks on ${days}`;
    case "monthly":
      return props.interval === 1 ? "Monthly" : `Every ${props.interval} months`;
    case "yearly":
      return "Yearly";
    default:
      return type || "Recurring";
  }
}

function formatResponseStatus(status) {
  const statusMap = {
    none: "No response",
    organizer: "Organizer",
    tentative: "Tentative",
    accepted: "Accepted",
    declined: "Declined",
  };
  return statusMap[status] || status;
}

function sanitizeFilename(name) {
  return name
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, "_")
    .substring(0, 100);
}

function sanitizeFilenameForObsidian(name) {
  // Keep spaces, just remove characters invalid for file names
  return name
    .replace(/[<>:"/\\|?*#^[\]]/g, "-")
    .trim()
    .substring(0, 100);
}

function removeTeamsDialInInfo(text) {
  if (!text) return text;

  // Simple approach: find "Microsoft Teams" and remove everything from there
  // Look for common start markers
  const startMarkers = [
    "________________",
    "----------------",
    "Microsoft Teams",
  ];

  let result = text;

  for (const marker of startMarkers) {
    const idx = result.indexOf(marker);
    if (idx !== -1 && result.includes("Microsoft Teams")) {
      // Found Teams section, remove from this marker to end
      result = result.substring(0, idx);
      break;
    }
  }

  // Clean up excessive newlines left behind
  result = result.replace(/\n{3,}/g, "\n\n").trim();

  return result;
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function showStatus(message, type) {
  const statusEl = document.getElementById("status-message");
  statusEl.textContent = message;
  statusEl.className = "status-message " + type;

  // Auto-hide after 3 seconds
  setTimeout(() => {
    statusEl.textContent = "";
    statusEl.className = "status-message";
  }, 3000);
}

export { exportToMarkdown, sendToObsidian };
